import { ref, reactive, onMounted } from 'vue';
import { getFeatureFlagProvider } from '@/lib/featureFlag';

// We do this to allow to import the feature flags directly from the composable
export * from '@/lib/featureFlag/featureFlags';

const toCamelCase = (str) => {
  return str.toLowerCase().replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
};

/**
 * A composable for managing reactive feature flags.
 *
 * Automatically creates camelCased reactive properties for each provided feature flag constant.
 *
 * @param {Array<string>} featureFlagConstants - An array of feature flag constants.
 * @returns {Object} A reactive object with feature flags mapped to their corresponding camelCased attribute names.
 *
 * @example
 *
 * import {
 *   useFeatureFlags,
 *   MILESTONE_ONE_FEED_ENABLED,
 *   MILESTONE_NEW_SIGNUP_ENABLED
 * } from '@/composables/useFeatureFlags';
 *
 * setup() {
 *   const {
 *     milestoneOneFeedEnabled,
 *     milestoneNewSignupEnabled
 *   } = useFeatureFlags([
 *     MILESTONE_ONE_FEED_ENABLED,
 *     MILESTONE_NEW_SIGNUP_ENABLED
 *   ]);
 *
 *   return {
 *     milestoneOneFeedEnabled,
 *     milestoneNewSignupEnabled
 *   };
 * }
 */
export function useFeatureFlags(featureFlagConstants) {
  const reactiveFlags = reactive({});

  featureFlagConstants.forEach((featureFlag) => {
    const attribute = toCamelCase(featureFlag);
    reactiveFlags[attribute] = ref(false);
  });

  const calculateValueOfReactiveFeatureFlags = () => {
    const featureFlagProvider = getFeatureFlagProvider();

    featureFlagConstants.forEach((featureFlag) => {
      const attribute = toCamelCase(featureFlag);
      reactiveFlags[attribute].value = featureFlagProvider.isFeatureEnabled(featureFlag);
    });
  };

  onMounted(calculateValueOfReactiveFeatureFlags);

  return reactiveFlags;
}
